import React, { useCallback, useEffect, useRef } from "react";
import CustomToolTip from "../../../../../../../CommonComponents/CustomToolTip";
import LayersPopup from "../../../../../../../CommonComponents/LayersPopup/LayersPopup";
import { QualitySettingsIcon } from "./ThermalHeatMapSvgs";

export interface RenderingQualitySettings {
    dprIndex: 0 | 1 | 2;
    antialiasing: boolean;
    floorGridEnabled: boolean;
    refreshRateMs: 1000 | 10000;
}

interface ThermalRenderingQualityBtnProps {
    settings: RenderingQualitySettings;
    onSettingsChange: (settings: RenderingQualitySettings) => void;
    onOpen?: () => void;
}

const ThermalRenderingQualityBtn: React.FC<ThermalRenderingQualityBtnProps> = ({ settings, onSettingsChange, onOpen }) => {
    const popupRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
                const button = popupRef.current.querySelector('button');
                if (button && button.classList.contains('active')) {
                    button.click();
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleToggle = useCallback((id: string) => {
        if (id === "dpr_low") {
            onSettingsChange({ ...settings, dprIndex: 0 });
        } else if (id === "dpr_medium") {
            onSettingsChange({ ...settings, dprIndex: 1 });
        } else if (id.startsWith("refresh_")) {
            const rateMap = { refresh_live: 1000, refresh_5s: 10000 } as const;
            onSettingsChange({ ...settings, refreshRateMs: rateMap[id as keyof typeof rateMap] });
        } else if (id === "antialiasing") {
            onSettingsChange({ ...settings, antialiasing: !settings.antialiasing });
        } else if (id === "floor_grid") {
            onSettingsChange({ ...settings, floorGridEnabled: !settings.floorGridEnabled });
        }
    }, [settings, onSettingsChange]);

    const items = [
        { id: "refresh_5s", label: "Low refresh rate", checked: settings.refreshRateMs === 10000 },
        { id: "refresh_live", label: "High refresh rate", checked: settings.refreshRateMs === 1000 },
        { id: "dpr_low", label: "Low quality", checked: settings.dprIndex === 0, dividerBefore: true },
        { id: "dpr_medium", label: "Default quality", checked: settings.dprIndex === 1 },
        { id: "antialiasing", label: "Anti-aliasing", checked: settings.antialiasing, dividerBefore: true },
        { id: "floor_grid", label: "Floor Grid", checked: settings.floorGridEnabled },
    ];

    return (
        <div ref={popupRef}>
            <LayersPopup
                items={items}
                onToggle={handleToggle}
                title="Rendering Quality"
                placement="left"
                renderTrigger={({ isOpen: popupIsOpen, onToggle }) => (
                    <CustomToolTip title="Rendering Quality Settings" placement="left">
                        <button
                            className={`thm-pb-btn thm-pb-quality${popupIsOpen ? " active" : ""}`}
                            onClick={() => {
                                if (!popupIsOpen) onOpen?.();
                                onToggle();
                            }}
                        >
                            <QualitySettingsIcon active={popupIsOpen} />
                        </button>
                    </CustomToolTip>
                )}
            />
        </div>
    );
};

export default React.memo(ThermalRenderingQualityBtn);
